/**
 * client/src/app/lazy-pages.ts
 * All React.lazy() page declarations for the router.
 * Extracted from App.tsx (300-line modularity rule).
 */

import { lazy } from "react";

// Core auth / onboarding pages
export const AuthEntry      = lazy(() => import("@/pages/auth-entry"));
// Authed "add a backup passkey" entry (#1437 BackupKeyStep, bug #1440 fix) —
// rendered by the dedicated /auth/add-key route (App.tsx), guarded the same
// way as /setup and /bank/add. See AddKeyEntry.tsx header.
export const AddKeyEntry    = lazy(() => import("@/pages/auth-entry/AddKeyEntry"));
export const Setup          = lazy(() => import("@/pages/setup"));
export const SetupGuardians = lazy(() => import("@/pages/setup/SetupGuardianPage"));
// Bespoke memory-seeding ceremony (#1221, PR C) — dark behind the
// `bespoke_seeding` API flag (PR B), so route registration is safe.
export const BespokeSeeding = lazy(() => import("@/pages/bespoke-seeding"));
// Day-2 quick-fire reconfirm screen (Spark Deck redesign, Module 03, AC-20) —
// same flag posture as BespokeSeeding (the underlying GET/POST routes 404
// while `bespoke_seeding` is off), so route registration is safe here too.
export const BespokeSeedReconfirm = lazy(() => import("@/pages/bespoke-seed-reconfirm"));
export const MemoryVault     = lazy(() => import("@/pages/memories"));
export const GuardianSetup  = lazy(() => import("@/pages/guardian-setup"));
export const GuardianAccountPage = lazy(() => import("@/pages/guardian-account"));
export const Recovery       = lazy(() => import("@/pages/recovery"));
// Ward-facing keep/veto interrupt for a guardian-initiated recovery claim
// (Task 5b, AC-12) — landed on directly from sign-in completion, not linked
// from the Recovery hub above.
export const ClaimPendingPage = lazy(() => import("@/pages/recovery/claim-pending"));
export const SecurityCentre = lazy(() => import("@/pages/security-centre"));
// Security Keys — split out of Security Overview into its own route, a
// sibling entry in the section nav (security-section-tabs.tsx). Owner
// direction, 2026-08-10.
export const SecurityKeys   = lazy(() => import("@/pages/security-keys"));
export const SecurityRecovery = lazy(() => import("@/pages/security-recovery"));
// UI-simplification wave, module 01 (#1792): the missing Settings door.
export const SettingsPage    = lazy(() => import("@/pages/settings"));

// Phase C: Onboarding + Challenge pages
export const CounterpartyInvitePage   = lazy(() => import("@/pages/onboarding/CounterpartyInvitePage"));
// Staged org founding (#1711 AC-3/AC-4, Task 5) — deferred org-creation step
// reached from the accept success screen's "Set up <name>" card or the
// dashboard "Finish setting up <name>" prompt card.
export const FoundOrgPage             = lazy(() => import("@/pages/onboarding/FoundOrgPage"));
// Platform operator → org invite landing (public; token + out-of-band code)
export const PlatformInvitePage       = lazy(() => import("@/pages/platform-invite/index"));
export const GuardianInvitePage       = lazy(() => import("@/pages/onboarding/GuardianInvitePage"));
// Device linking (#2210, #2280): the NEW device's claim screen (public — the
// device is logged out by definition). The approver's page is gone: after the
// direction flip the approval happens inside Security Centre's add-device
// dialog, which never resolves a scanned token or a typed code.
export const LinkDevicePage           = lazy(() => import("@/pages/link-device/index"));
export const PathwayStatusPage        = lazy(() => import("@/pages/PathwayStatusPage"));

// Wave 3: Dashboard + Admin Platform pages
export const Dashboard        = lazy(() => import("@/pages/dashboard"));
export const AdminPlatform    = lazy(() => import("@/pages/admin-platform"));
// Operator Activity (module 02 task 3) — no longer a dashboard tab. It is a
// DESTINATION at `/admin/platform/activity`, mounted inside the same admin
// shell chrome as the console itself. The component file keeps its "-tab"
// name because it is still the same thin wrapper around
// PlatformActivityConsole; only its mount point moved.
export const PlatformActivity = lazy(() => import("@/pages/dashboard/components/platform-activity-tab"));
// One person as seen from ONE organisation (module 06 Task 3) - the screen
// `EntityLink`'s `person` kind has been pointing at since module 04.
export const PlatformPersonPage = lazy(() => import("@/pages/dashboard/components/platform-person"));
export const VaultIdentifiers = lazy(() => import("@/pages/vault-identifiers"));
// VaultIdentifierHistory removed — /vault-identifier-history/:id now redirects to
// /audit?identifier=:id&preset=identifier (spec AC-8, Wave "Unified Activity view").

// Payment Rail: Invite landing
export const InviteLandingPage = lazy(() => import("@/pages/invite/invite-landing"));

// Payment Rail: Add bank account ceremony
export const AddBankPage = lazy(() => import("@/pages/bank/add-bank"));

// Wave 6.1b-3: Multi-sig auth requests
export const AuthRequestListPage     = lazy(() => import("@/pages/auth-requests/AuthRequestListPage"));
export const AuthRequestApprovalPage = lazy(() => import("@/pages/auth-requests/AuthRequestApprovalPage"));

// Wave 6.1c: View separation — placeholder pages (full impl in 6.1c-7 / 6.1c-8)
export const AuditPage       = lazy(() => import("@/pages/audit"));
export const AuditGroupsPage = lazy(() => import("@/pages/audit/groups"));
export const OrgVaultPage    = lazy(() => import("@/pages/org-vault"));

// Task 24 (#245): staff payout UI — client-side ABA assembly + hash-only attestation
export const OrgVaultPayoutsPage = lazy(() => import("@/pages/org-vault/payouts"));

// #1627: second-officer approve/review surface for a staged ABA payout quorum request
export const OrgVaultPayoutApprovePage = lazy(() => import("@/pages/org-vault/payout-approve"));

// #1627 FIX 1: initiator's re-openable view of a staged request (status + download)
export const OrgVaultPayoutRequestPage = lazy(() => import("@/pages/org-vault/payout-request"));

// Unified Connections view — shared by personal + org context
export const ConnectionsPage = lazy(() => import("@/pages/connections"));

// Invitations — decisions waiting on this person, kept off Connections (#2171)
export const InvitationsPage = lazy(() => import("@/pages/invitations"));

// Wave 6.1d: Standalone Alerts page (AC-13, AC-14, AC-15, AC-16)
export const AlertsPage = lazy(() => import("@/pages/alerts"));

// Guardian step-up ceremony page — closes #654 (route wire-up so liveness redirect lands on the page, not 404)
export const GuardianStepUpPage = lazy(() => import("@/pages/guardian-stepup"));

// Guardian recovery-approval page (PR 1 Task 5 [AC-3], refs #2257): mounts
// the previously-orphaned GuardianApprovalCard behind honest terminal states.
export const RecoveryApprovalPage = lazy(() => import("@/pages/recovery-approval/index"));

// Guardian Review page — "prompted every 6 months at login" (Phase A Item
// 10b). Built end-to-end against live server routes (server/routes/
// guardian-review.routes.ts) but never mounted; wired up here + the
// dashboard/security-centre login-time notice that links to it.
export const GuardianReview = lazy(() => import("@/pages/guardian-review"));

// Wave 7b / Plan B: Investor challenge inbox (refs #235)
export const ChallengesPage       = lazy(() => import("@/pages/challenges/index"));
export const ChallengeRespondPage = lazy(() => import("@/pages/challenges/[id]/respond"));
// Per-challenge conversation thread — one challenge's request → response in its
// own window (reached from any inbox card via ViewConversationLink).
export const ChallengeThreadPage  = lazy(() => import("@/pages/challenges/[id]/thread"));
// Wave 7b / #239: "Review last 30 days" retrospective flags card
export const RetrospectivePage    = lazy(() => import("@/pages/challenges/[id]/retrospective"));

// Wave 7b / #236: Operator issued-challenges dashboard
export const IssuedChallengesPage = lazy(() => import("@/pages/challenges/issued"));
export const NewChallengePage = lazy(() => import("@/pages/challenges/new"));
export const WorkflowNewPage = lazy(() => import("@/pages/challenges/workflows/new"));
export const WorkflowListPage = lazy(() => import("@/pages/challenges/workflows"));
export const WorkflowDetailPage = lazy(() => import("@/pages/challenges/workflows/detail"));

// Communications overhaul Wave D: per-counterparty conversation view + thread
export const ConversationsPage      = lazy(() => import("@/pages/challenges/conversations"));
export const ConversationThreadPage = lazy(() => import("@/pages/challenges/conversation-thread"));

// Group Conversations W3 (#1666): create flow + the N-party thread page.
export const GroupConversationCreatePage = lazy(() => import("@/pages/challenges/GroupConversationCreate"));
export const GroupConversationThreadPage = lazy(() => import("@/pages/challenges/conversation-thread-group"));

// Internal Team Conversations, module 03 (D8): the internal-thread create
// flow. The thread page itself REUSES GroupConversationThreadPage above
// (scope="internal" prop) rather than a new lazy import — no forked component.
export const InternalConversationCreatePage = lazy(
  () => import("@/pages/challenges/conversations/internal-conversation-create"),
);

// Messaging Experience Module 03 (#1715, AC-20/AC-21): the unified "+ New"
// entry point — replaces the split "+ New group" / "+ New communication" CTAs.
export const ConversationsNewEntryPage = lazy(() => import("@/pages/challenges/ConversationsNewEntry"));

// Unified Communications (Task 6 / AC-16): the ONE record screen, keyed by
// communicationId, dispatching by underlying construct under shared chrome.
export const CommRecordScreen       = lazy(() => import("@/pages/challenges/comm-record-screen"));

// Unified Communications (AC-5): the inbox compose door — UnifiedComposer in inbox mode.
// RETIRES the old kind-chooser (compose.tsx); the composer itself is the one entry.
export const ComposePage            = lazy(() => import("@/pages/challenges/compose-page"));

// Wave 7b / #237: Reverse challenge launcher (investor → operator)
export const NewReverseChallengePage = lazy(() => import("@/pages/challenges/new-reverse"));

// Communications Wave 5 / #1111: standalone payment_request compose
export const NewPaymentRequestPage = lazy(() => import("@/pages/challenges/NewPaymentRequest"));

// Deal entity (Task 4): org-context deals list + detail
export const DealsPage      = lazy(() => import("@/pages/deals/index"));
export const DealDetailPage = lazy(() => import("@/pages/deals/[dealId]"));

// Recovery hardening #1239: pre-auth Official Channels reference page (AC-REC-02)
export const OfficialChannelsPage = lazy(() => import("@/pages/official-channels"));
// Product changelog (#3478): signed-in only, inside the app shell (owner directive 2026-10-03)
export const ChangelogPage = lazy(() => import("@/pages/changelog"));
